/* eslint-disable no-shadow */
/* eslint-disable no-alert */
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import _uniqBy from "lodash/uniqBy";
import _cloneDeep from "lodash/cloneDeep";
import _orderBy from "lodash/orderBy";
import _get from "lodash/get";
import BeneficialOwnersTable from "./BeneficialOwnersTable";
import BeneficialOwnerSelection from "./BeneficialOwnerSelection";
import ModalWrapper from "components/lib/Shared/ModalWrapper";
import { openModal } from "state/slices/modals";
import {
  addBeneficialOwnerAsync,
  deleteBeneficialOwnerAsync,
  updateDropdownEntryStatusAsync,
} from "state/slices/tables/beneficialOwnersTableSlice";
import { CORPORATES, INDIVIDUALS } from "../SFLdata/config/constants";
import {
  addMultipleCorporateShareholder,
  resetCorporateShareholderTable,
} from "state/slices/tables/corporateShareholderTableSlice";
import Tabs from "components/lib/Shared/Tabs";
import {
  PlusIcon,
  ChevronLeftIcon,
  ExclamationIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ScaleIcon,
  TrashIcon,
  PencilIcon,
} from "@heroicons/react/outline";
import {
  calculateCumulativeOwnership,
  delay,
  groupBeneficialOwners,
} from "utils";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { useParams } from "react-router-dom";
import Overlay from "components/lib/Shared/Overlay";
import CapitalChangeWarning from "./CapitalChangeWarning";

const tabs = [
  { id: "reportableBO", label: "Reportable BO" },
  { id: "nonReportableBO", label: "Non-Reportable BO" },
];

const BeneficialOwnershipTab = ({
  handleBackBtn,
  handleNextBtn,
  submitBtnLabel,
  isSubmitting,
  isInUpdateCompanyDetailsContext,
  transactions,
}) => {
  const { companyId } = useParams();
  const [showBeneficialOwnersBtn, setShowBeneficialOwnersBtn] = useState(true);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const isFirstStep = selectedTabIndex === 0;
  const isLastStep = selectedTabIndex === tabs.length - 1;
  const { modals } = useSelector((state) => state.modalsSlice);
  const [mode, setMode] = useState({ id: "", editing: false });
  const dispatch = useDispatch();
  const [currentOfficial, setCurrentOfficial] = useState({});
  const [isViewOnly, setIsViewOnly] = useState(false);
  const {
    beneficialOwners,
    deleteBeneficialOwnerStatus,
    addBeneficialOwnerStatus,
    getAllBeneficialOwnersStatus,
    updateDropdownEntryStatusStatus,
  } = useSelector((state) => state.beneficialOwnersTableSlice);

  const { companyOfficials } = useSelector(
    (state) => state.companyOfficialsSlice
  );

  const [reportableBO, setReportableBO] = useState([]);
  const [nonReportableBO, setNonReportableBO] = useState([]);
  const [reportableBOWithChanges, setReportableBOWithChanges] = useState([]);
  const [nonReportableBOWithChanges, setNonReportableBOWithChanges] = useState(
    []
  );
  const [dropdownBOWithChanges, setDropdownBOWithChanges] = useState([]);
  const [selectedCorporateIDType, setSelectedCorporateIDType] = useState(null);
  let beneficialOwnersDropdown = beneficialOwners.filter(
    (bo) => bo?.showInBoDropdown
  );
  const [selectedDropdownBO, setSelectedDropdownBO] = useState(false);
  const [boTabValidationError, setBoTabValidationError] = useState("");
  const { shares: nominalShareCapital } = useSelector(
    (state) => state.sharesTableSlice
  );
  const [hasUnmetConditions, setHasUnmetConditions] = useState(false);
  const [officialsWithShareChanges, setOfficialsWithShareChanges] = useState(
    new Map()
  );
  const [parentsNeedingAttention, setParentsNeedingAttention] = useState([]);
  const [dismissedWarningIds, setDismissedWarningIds] = useState([]);
  const [showAllWarnings, setShowAllWarnings] = useState(false);

  const allBOs = [...beneficialOwners, ...beneficialOwnersDropdown];
  const companyOfficialsSlice = useSelector(
    (state) => state.companyOfficialsSlice
  );

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Extract BO changes from transactions
   */
  const extractBOChangesFromTransactions = () => {
    if (!transactions || transactions.length === 0) return {};

    const boChangesMap = {};

    transactions.forEach((transaction) => {
      if (
        transaction.shareCapitalChanges &&
        Array.isArray(transaction.shareCapitalChanges)
      ) {
        transaction.shareCapitalChanges.forEach((shareChange) => {
          if (
            shareChange.allocations &&
            Array.isArray(shareChange.allocations)
          ) {
            shareChange.allocations.forEach((allocation) => {
              if (
                allocation.officialId &&
                allocation.beneficialOwnershipDetails
              ) {
                boChangesMap[allocation.officialId] = {
                  ...allocation.beneficialOwnershipDetails,
                  _transactionId: transaction._id,
                  _changeType: shareChange.changeType,
                };
              }
            });
          }
        });
      }
    });

    return boChangesMap;
  };

  /**
   * Merge BO with transaction changes
   */
  const mergeBOWithTransactionChanges = (bo, boChangesMap) => {
    const changes = boChangesMap[bo._id];
    if (!changes) return bo;

    const clonedBO = _cloneDeep(bo);

    if (changes.updatedDetails) {
      clonedBO.beneficialOwnershipForm = {
        ...clonedBO.beneficialOwnershipForm,
        ...changes.updatedDetails,
      };
    }

    clonedBO._changedFields = changes.changedFields || {};
    clonedBO._hasTransactionChanges = true;
    clonedBO._transactionId = changes._transactionId;
    clonedBO._transactionChangeData = changes;
    clonedBO._isChangedParent = true;

    return clonedBO;
  };

  /**
   * Calculate suggested redistribution based on scale factor
   */
  const calculateSuggestedRedistribution = (
    parent,
    children,
    oldTotal,
    newTotal
  ) => {
    if (!children || children.length === 0) return [];

    const scaleFactor = newTotal / oldTotal;
    const changeDirection = newTotal > oldTotal ? "increase" : "decrease";

    return children.map((child) => {
      const currentPercent =
        child.beneficialOwnershipForm?.indirectPercentShareholding || 0;
      const suggestedPercent = currentPercent * scaleFactor;

      return {
        ...child,
        _suggestedPercentage: suggestedPercent,
        _oldPercentage: currentPercent,
        _changeDirection: changeDirection,
        _changeMagnitude: Math.abs(suggestedPercent - currentPercent),
      };
    });
  };

  /**
   * NEW: Suggest which children to remove for capital decrease
   */
  const suggestShareholdersToRemove = (children, targetTotal, currentTotal) => {
    const reductionNeeded = currentTotal - targetTotal;

    if (reductionNeeded <= 0) return null; // Not a reduction scenario

    // Sort children by ownership (smallest first)
    const sortedChildren = [...children].sort(
      (a, b) =>
        (a.beneficialOwnershipForm?.indirectPercentShareholding || 0) -
        (b.beneficialOwnershipForm?.indirectPercentShareholding || 0)
    );

    let removed = [];
    let removedTotal = 0;

    // Greedy algorithm: remove smallest shareholders until reduction met
    for (const child of sortedChildren) {
      const childPercent =
        child.beneficialOwnershipForm?.indirectPercentShareholding || 0;

      if (removedTotal + childPercent <= reductionNeeded + 0.01) {
        // Small epsilon
        removed.push(child);
        removedTotal += childPercent;
      }

      if (Math.abs(removedTotal - reductionNeeded) < 0.01) break;
    }

    const remaining = sortedChildren.filter((c) => !removed.includes(c));
    const remainingTotal = remaining.reduce(
      (sum, c) =>
        sum + (c.beneficialOwnershipForm?.indirectPercentShareholding || 0),
      0
    );

    return {
      toRemove: removed,
      removedTotal,
      remaining,
      remainingTotal,
      exactMatch: Math.abs(removedTotal - reductionNeeded) < 0.01,
      targetTotal,
    };
  };

  /**
   * NEW: Find best removal combination for exact match
   */
  const findBestRemovalCombination = (children, targetTotal) => {
    const currentTotal = children.reduce(
      (sum, c) =>
        sum + (c.beneficialOwnershipForm?.indirectPercentShareholding || 0),
      0
    );

    // Try all combinations (using bit manipulation for power set)
    const n = children.length;
    let bestMatch = null;
    let bestDifference = Infinity;

    for (let i = 0; i < 1 << n; i++) {
      const combination = [];
      let comboTotal = 0;

      for (let j = 0; j < n; j++) {
        if (i & (1 << j)) {
          combination.push(children[j]);
          comboTotal +=
            children[j].beneficialOwnershipForm?.indirectPercentShareholding ||
            0;
        }
      }

      const difference = Math.abs(comboTotal - targetTotal);

      if (difference < bestDifference) {
        bestDifference = difference;
        bestMatch = {
          toKeep: combination,
          toRemove: children.filter((c) => !combination.includes(c)),
          keepTotal: comboTotal,
          exactMatch: difference < 0.01,
        };
      }

      // Early exit if exact match found
      if (difference < 0.01) break;
    }

    return bestMatch;
  };

  /**
   * Mark children of changed parents
   */
  const markChildrenOfChangedParents = (
    beneficialOwnersList,
    boChangesMap,
    officialsWithShareChangesMap
  ) => {
    const changedParentIds = Object.keys(boChangesMap);

    return beneficialOwnersList.map((bo) => {
      const parentId =
        bo.linkToCompany || bo.linkToCompany?._id || bo.shareholderWithBO;

      let parentTransactionId = null;
      if (boChangesMap[parentId]) {
        parentTransactionId = boChangesMap[parentId]._transactionId;
      } else if (
        officialsWithShareChangesMap &&
        officialsWithShareChangesMap.has(parentId)
      ) {
        parentTransactionId = officialsWithShareChangesMap.get(parentId);
      }

      const isChildOfChangedParent =
        changedParentIds.some((id) => id === parentId) ||
        (officialsWithShareChangesMap &&
          officialsWithShareChangesMap.has(parentId));

      if (isChildOfChangedParent) {
        if (bo._hasTransactionChanges) return bo;

        const clonedBO = _cloneDeep(bo);
        clonedBO._isChildOfChangedParent = true;
        clonedBO._parentHasChanges = true;
        clonedBO._needsReview = true;
        if (parentTransactionId) clonedBO._transactionId = parentTransactionId;
        return clonedBO;
      }

      return bo;
    });
  };

  /**
   * Remove dropdown shareholder if conditions met
   */
  const removeDropdownShareholder = (dropdownShareholder) => {
    const dropdownBO = _cloneDeep(dropdownShareholder);

    if (!dropdownBO?.beneficialOwnershipForm) return;
    const {
      directPercentShareholding = 0,
      indirectPercentShareholding = 0,
      directPercentVotingRights = 0,
      indirectPercentVotingRights = 0,
      directRightRemoveDirector = "no",
      indirectRightRemoveDirector = "no",
      directCompanyControlRight = "no",
      indirectCompanyControlRight = "no",
    } = dropdownBO.beneficialOwnershipForm;

    const linkedBOs = allBOs.filter(
      (bo) =>
        bo?.linkToCompany === dropdownBO._id ||
        bo?.linkToCompany?._id === dropdownBO._id
    );
    const {
      allottedIndirectShares,
      allottedIndirectVotingRights,
      allottedRemoveDirector,
      allottedControlRight,
    } = linkedBOs.reduce(
      (acc, bo) => ({
        allottedIndirectShares:
          acc.allottedIndirectShares +
          Number(bo.beneficialOwnershipForm?.directPercentShareholding ?? 0) +
          Number(bo.beneficialOwnershipForm?.indirectPercentShareholding ?? 0),
        allottedIndirectVotingRights:
          acc.allottedIndirectVotingRights +
          Number(bo.beneficialOwnershipForm?.directPercentVotingRights ?? 0) +
          Number(bo.beneficialOwnershipForm?.indirectPercentVotingRights ?? 0),
        allottedRemoveDirector:
          acc.allottedRemoveDirector ||
          bo.beneficialOwnershipForm?.directRightRemoveDirector === "yes" ||
          bo.beneficialOwnershipForm?.indirectRightRemoveDirector === "yes",
        allottedControlRight:
          acc.allottedControlRight ||
          bo.beneficialOwnershipForm?.directCompanyControlRight === "yes" ||
          bo.beneficialOwnershipForm?.indirectCompanyControlRight === "yes",
      }),
      {
        allottedIndirectShares: 0,
        allottedIndirectVotingRights: 0,
        allottedRemoveDirector: false,
        allottedControlRight: false,
      }
    );

    const sharesMatch =
      Math.abs(
        allottedIndirectShares -
          (Number(indirectPercentShareholding) +
            Number(directPercentShareholding))
      ) < 0.01;
    const votingMatch =
      Math.abs(
        allottedIndirectVotingRights -
          (Number(indirectPercentVotingRights) +
            Number(directPercentVotingRights))
      ) < 0.01;
    const removeDirectorMatch =
      (directRightRemoveDirector === "yes" ||
        indirectRightRemoveDirector === "yes") === allottedRemoveDirector;
    const controlMatch =
      (directCompanyControlRight === "yes" ||
        indirectCompanyControlRight === "yes") === allottedControlRight;

    if (
      dropdownBO.idType === "Estate" &&
      sharesMatch &&
      votingMatch &&
      linkedBOs.length > 0
    ) {
      dropdownBO["showInBoTable"] = true;
      const alreadyInBOTable = beneficialOwners.some(
        (bo) => bo._id === dropdownBO._id
      );
      if (!alreadyInBOTable)
        dispatch(addBeneficialOwnerAsync({ companyId, data: dropdownBO }));
      dispatch(
        updateDropdownEntryStatusAsync({
          beneficialOwnerId: dropdownBO._id,
          data: { showInBoDropdown: false },
        })
      );
    } else if (
      sharesMatch &&
      votingMatch &&
      removeDirectorMatch &&
      controlMatch &&
      linkedBOs.length > 0
    ) {
      dropdownBO["showInBoTable"] = true;
      const alreadyInBOTable = beneficialOwners.some(
        (bo) => bo._id === dropdownBO._id
      );
      if (!alreadyInBOTable)
        dispatch(addBeneficialOwnerAsync({ companyId, data: dropdownBO }));
      dispatch(
        updateDropdownEntryStatusAsync({
          beneficialOwnerId: dropdownBO._id,
          data: { showInBoDropdown: false },
        })
      );
    }
  };

  // ============================================================================
  // MAIN USEEFFECT: CATEGORIZE BOs (NON-TRANSACTION CONTEXT)
  // ============================================================================

  useEffect(() => {
    beneficialOwnersDropdown.map(removeDropdownShareholder);

    let tempReportableBO = [];
    let tempNonReportableBO = [];
    if (beneficialOwners.length > 0) {
      beneficialOwners.forEach((bo) => {
        if (!bo) return;

        const {
          directPercentShareholding,
          indirectPercentShareholding,
          directPercentVotingRights,
          indirectPercentVotingRights,
          directRightRemoveDirector,
          indirectRightRemoveDirector,
          directCompanyControlRight,
          indirectCompanyControlRight,
        } = bo?.beneficialOwnershipForm ?? {};

        const isDirectBeneficiary =
          directPercentShareholding >= 10 ||
          directPercentVotingRights >= 10 ||
          directRightRemoveDirector === "yes" ||
          directCompanyControlRight === "yes";
        const isIndirectBeneficiary =
          indirectPercentShareholding >= 10 ||
          indirectPercentVotingRights >= 10 ||
          indirectRightRemoveDirector === "yes" ||
          indirectCompanyControlRight === "yes";

        if (bo?.idType === "Minority") {
          bo = {
            ...bo,
            hidden: true,
          };
        }

        if (!bo?.hidden === true) {
          const parentBO = bo?.shareholderWithBO
            ? beneficialOwners.find(
                (official) => official._id === bo.shareholderWithBO
              )
            : {};
          if (
            INDIVIDUALS.includes(bo.idType) &&
            bo.isReportable &&
            (CORPORATES.includes(parentBO?.idType)
              ? isDirectBeneficiary
              : isDirectBeneficiary || isIndirectBeneficiary) &&
            bo.isEstateDistributionDetermined !== "No" &&
            bo.idType !== "Estate"
          ) {
            tempReportableBO.push(bo);
          } else tempNonReportableBO.push(bo);
        }
      });
    }

    const verifyKeys = [
      "identificationDetails.nationalIDNumber",
      "identificationDetails.foreignCertificateID",
      "identificationDetails.passportNumber",
      "identificationDetails.birthCertificateNumber",
    ];

    let filteredTempReportableBOs = [...tempReportableBO];
    let filteredTempNonReportableBOs = [...tempNonReportableBO];

    verifyKeys.map((verifyKey) => {
      tempNonReportableBO.map((bo) => {
        const hasReportableInstance = tempReportableBO.find(
          (reportableBoRow) =>
            _get(reportableBoRow, verifyKey) &&
            _get(reportableBoRow, verifyKey) === _get(bo, verifyKey)
        );
        if (hasReportableInstance) {
          filteredTempReportableBOs.push(bo);
          const boIndex = filteredTempNonReportableBOs.findIndex(
            (bo) =>
              _get(bo, verifyKey) === _get(hasReportableInstance, verifyKey)
          );
          filteredTempNonReportableBOs.splice(boIndex, 1);
        }
      });
    });

    filteredTempReportableBOs = groupBeneficialOwners(
      _orderBy(
        filteredTempReportableBOs,
        ["shareholderWithBO", "linkToCompany"],
        ["desc", "desc"]
      )
    );

    const jointShareholdersNonReportableBOs =
      filteredTempNonReportableBOs.filter(
        (official) =>
          official.idType === "Joint Shareholder" || official?.JSGroupID
      );

    filteredTempNonReportableBOs = groupBeneficialOwners(
      _orderBy(
        filteredTempNonReportableBOs.filter(
          (official) =>
            official.idType !== "Joint Shareholder" && !official?.JSGroupID
        ),
        ["shareholderWithBO", "linkToCompany"],
        ["desc", "desc"]
      )
    );

    tempReportableBO = filteredTempReportableBOs;
    tempNonReportableBO = filteredTempNonReportableBOs;

    const reportableBOAfterCumulative = tempNonReportableBO
      .filter((bo) => INDIVIDUALS.includes(bo.idType))
      .filter((bo) => {
        const { cumulativeShareholdings, cumulativeVotings } =
          calculateCumulativeOwnership(bo, beneficialOwners);
        if (cumulativeShareholdings >= 10 || cumulativeVotings >= 10) {
          const indexOfBO = tempNonReportableBO.findIndex(
            (beneficialOwner) => beneficialOwner._id === bo._id
          );
          filteredTempNonReportableBOs.splice(indexOfBO, 1);
          return true;
        }
      });

    setReportableBO([
      ...filteredTempReportableBOs,
      ...reportableBOAfterCumulative,
    ]);
    setNonReportableBO([
      ...filteredTempNonReportableBOs,
      ...jointShareholdersNonReportableBOs,
    ]);
  }, [beneficialOwners, setReportableBO, setNonReportableBO]);

  // ============================================================================
  // MAIN USEEFFECT: TRANSACTION CONTEXT WITH CAPITAL CHANGE LOGIC
  // ============================================================================

  useEffect(() => {
    if (
      !isInUpdateCompanyDetailsContext ||
      !transactions ||
      transactions.length === 0
    ) {
      setReportableBOWithChanges(reportableBO);
      setNonReportableBOWithChanges(nonReportableBO);
      return;
    }

    const boChangesMap = extractBOChangesFromTransactions();

    const officialsWithShareChangesMap = new Map();
    transactions.forEach((transaction) => {
      if (
        transaction.shareCapitalChanges &&
        Array.isArray(transaction.shareCapitalChanges)
      ) {
        transaction.shareCapitalChanges.forEach((shareChange) => {
          if (
            shareChange.allocations &&
            Array.isArray(shareChange.allocations)
          ) {
            shareChange.allocations.forEach((allocation) => {
              if (allocation.officialId) {
                officialsWithShareChangesMap.set(
                  allocation.officialId,
                  transaction._id
                );
              }
            });
          }
        });
      }
    });
    setOfficialsWithShareChanges(officialsWithShareChangesMap);

    const officialsFromTransactions = [];
    transactions.forEach((transaction) => {
      if (
        transaction.shareCapitalChanges &&
        Array.isArray(transaction.shareCapitalChanges)
      ) {
        transaction.shareCapitalChanges.forEach((shareChange) => {
          if (
            shareChange.allocations &&
            Array.isArray(shareChange.allocations)
          ) {
            shareChange.allocations.forEach((allocation) => {
              if (
                allocation.officialId &&
                allocation.beneficialOwnershipDetails
              ) {
                const existsInBOs = beneficialOwners.some(
                  (bo) => bo._id === allocation.officialId
                );

                if (!existsInBOs) {
                  const officialData = companyOfficials?.items?.find(
                    (bo) => bo._id === allocation.officialId
                  );

                  if (officialData) {
                    const { updatedDetails, changedFields } =
                      allocation.beneficialOwnershipDetails;
                    const clonedOfficial = _cloneDeep(officialData);

                    clonedOfficial.beneficialOwnershipForm = {
                      ...clonedOfficial.beneficialOwnershipForm,
                      ...updatedDetails,
                    };

                    clonedOfficial._changedFields = changedFields || {};
                    clonedOfficial._hasTransactionChanges = true;
                    clonedOfficial._transactionId = transaction._id;
                    clonedOfficial._transactionChangeData =
                      allocation.beneficialOwnershipDetails;
                    clonedOfficial._isFromTransaction = true;

                    const hasIndirectControl =
                      (updatedDetails?.indirectCompanyControlRight &&
                        updatedDetails.indirectCompanyControlRight !== "no") ||
                      (updatedDetails?.indirectPercentVotingRights &&
                        updatedDetails.indirectPercentVotingRights > 0) ||
                      (updatedDetails?.indirectPercentShareholding &&
                        updatedDetails.indirectPercentShareholding > 0) ||
                      (updatedDetails?.indirectRightRemoveDirector &&
                        updatedDetails.indirectRightRemoveDirector !== "no");

                    if (hasIndirectControl) {
                      clonedOfficial.showInBoDropdown = true;
                      clonedOfficial.showInBoTable = false;
                    } else {
                      clonedOfficial.showInBoTable = true;
                      clonedOfficial.showInBoDropdown = false;
                    }

                    officialsFromTransactions.push(clonedOfficial);
                  }
                }
              }
            });
          }
        });
      }
    });

    let tempReportableBO = [];
    let tempNonReportableBO = [];
    let processedBOs = [];

    if (beneficialOwners.length > 0) {
      processedBOs = beneficialOwners
        .map((originalBO) => {
          if (!originalBO) return null;
          return mergeBOWithTransactionChanges(originalBO, boChangesMap);
        })
        .filter(Boolean);

      processedBOs = markChildrenOfChangedParents(
        processedBOs,
        boChangesMap,
        officialsWithShareChangesMap
      );

      processedBOs = [...processedBOs, ...officialsFromTransactions];

      processedBOs.forEach((bo) => {
        const {
          directPercentShareholding,
          indirectPercentShareholding,
          directPercentVotingRights,
          indirectPercentVotingRights,
          directRightRemoveDirector,
          indirectRightRemoveDirector,
          directCompanyControlRight,
          indirectCompanyControlRight,
        } = bo?.beneficialOwnershipForm ?? {};

        const isDirectBeneficiary =
          directPercentShareholding >= 10 ||
          directPercentVotingRights >= 10 ||
          directRightRemoveDirector === "yes" ||
          directCompanyControlRight === "yes";
        const isIndirectBeneficiary =
          indirectPercentShareholding >= 10 ||
          indirectPercentVotingRights >= 10 ||
          indirectRightRemoveDirector === "yes" ||
          indirectCompanyControlRight === "yes";

        if (bo?.idType === "Minority") {
          bo = {
            ...bo,
            hidden: true,
          };
        }

        if (!bo?.hidden === true) {
          const parentBO = bo?.shareholderWithBO
            ? processedBOs.find(
                (official) => official._id === bo.shareholderWithBO
              )
            : {};

          if (
            INDIVIDUALS.includes(bo.idType) &&
            bo.isReportable &&
            (CORPORATES.includes(parentBO?.idType)
              ? isDirectBeneficiary
              : isDirectBeneficiary || isIndirectBeneficiary) &&
            bo.isEstateDistributionDetermined !== "No" &&
            bo.idType !== "Estate"
          ) {
            tempReportableBO.push(bo);
          } else {
            tempNonReportableBO.push(bo);
          }
        }
      });
    }

    const verifyKeys = [
      "identificationDetails.nationalIDNumber",
      "identificationDetails.foreignCertificateID",
      "identificationDetails.passportNumber",
      "identificationDetails.birthCertificateNumber",
    ];

    let filteredTempReportableBOs = [...tempReportableBO];
    let filteredTempNonReportableBOs = [...tempNonReportableBO];

    verifyKeys.map((verifyKey) => {
      tempNonReportableBO.map((bo) => {
        const hasReportableInstance = tempReportableBO.find(
          (reportableBoRow) =>
            _get(reportableBoRow, verifyKey) &&
            _get(reportableBoRow, verifyKey) === _get(bo, verifyKey)
        );
        if (hasReportableInstance) {
          filteredTempReportableBOs.push(bo);
          const boIndex = filteredTempNonReportableBOs.findIndex(
            (bo) =>
              _get(bo, verifyKey) === _get(hasReportableInstance, verifyKey)
          );
          filteredTempNonReportableBOs.splice(boIndex, 1);
        }
      });
    });

    filteredTempReportableBOs = groupBeneficialOwners(
      _orderBy(
        filteredTempReportableBOs,
        ["shareholderWithBO", "linkToCompany"],
        ["desc", "desc"]
      )
    );

    const jointShareholdersNonReportableBOs =
      filteredTempNonReportableBOs.filter(
        (official) =>
          official.idType === "Joint Shareholder" || official?.JSGroupID
      );

    filteredTempNonReportableBOs = groupBeneficialOwners(
      _orderBy(
        filteredTempNonReportableBOs.filter(
          (official) =>
            official.idType !== "Joint Shareholder" && !official?.JSGroupID
        ),
        ["shareholderWithBO", "linkToCompany"],
        ["desc", "desc"]
      )
    );

    // ============================================================================
    // NEW: SMART VALIDATION FOR CAPITAL INCREASE/DECREASE
    // ============================================================================

    let conditionsMet = true;
    const parentIdsToCheck = new Set([...officialsWithShareChangesMap.keys()]);
    const parentsWithIssues = [];

    processedBOs.forEach((bo) => {
      if (bo.linkToCompany || bo.shareholderWithBO) {
        const parentId =
          bo.linkToCompany || bo.linkToCompany?._id || bo.shareholderWithBO;
        if (parentId) parentIdsToCheck.add(parentId);
      }
    });

    parentIdsToCheck.forEach((parentId) => {
      const parent =
        processedBOs.find((p) => p._id === parentId) ||
        allBOs.find((p) => p._id === parentId);
      if (!parent) return;

      const children = processedBOs.filter(
        (child) =>
          (child.linkToCompany === parentId ||
            child.linkToCompany?._id === parentId ||
            child.shareholderWithBO === parentId) &&
          !child.hidden
      );

      const parentForm = parent.beneficialOwnershipForm || {};
      const parentTotalShares = Number(
        parentForm.maxPercentShareholding ??
          Number(parentForm.directPercentShareholding || 0) +
            Number(parentForm.indirectPercentShareholding || 0)
      );
      const parentTotalVoting = Number(
        parentForm.maxPercentVotingRights ??
          Number(parentForm.directPercentVotingRights || 0) +
            Number(parentForm.indirectPercentVotingRights || 0)
      );

      const totalChildrenShares =
        children.reduce(
          (sum, child) =>
            sum +
            Number(
              child.beneficialOwnershipForm?.indirectPercentShareholding || 0
            ),
          0
        ) + Number(parentForm.directPercentShareholding || 0);

      const totalChildrenVoting =
        children.reduce(
          (sum, child) =>
            sum +
            Number(
              child.beneficialOwnershipForm?.indirectPercentVotingRights || 0
            ),
          0
        ) + Number(parentForm.directPercentVotingRights || 0);

      const sharesDifference = parentTotalShares - totalChildrenShares;
      const votingDifference = parentTotalVoting - totalChildrenVoting;

      const isShareMismatch = Math.abs(sharesDifference) > 0.01;
      const isVotingMismatch = Math.abs(votingDifference) > 0.01;

      const parentHasIndirectDirectorRight =
        parentForm.indirectRightRemoveDirector === "yes";
      const childrenHaveDirectorRight = children.some(
        (c) => c.beneficialOwnershipForm?.indirectRightRemoveDirector === "yes"
      );
      const isDirectorRightMismatch =
        parentHasIndirectDirectorRight && !childrenHaveDirectorRight;

      const parentHasIndirectControlRight =
        parentForm.indirectCompanyControlRight === "yes";
      const childrenHaveControlRight = children.some(
        (c) => c.beneficialOwnershipForm?.indirectCompanyControlRight === "yes"
      );
      const isControlRightMismatch =
        parentHasIndirectControlRight && !childrenHaveControlRight;

      if (
        isShareMismatch ||
        isVotingMismatch ||
        isDirectorRightMismatch ||
        isControlRightMismatch
      ) {
        conditionsMet = false;

        // ========================================================================
        // NEW LOGIC: Determine if INCREASE or DECREASE
        // ========================================================================

        const isIncrease = sharesDifference > 0.01 || votingDifference > 0.01;
        const isDecrease = sharesDifference < -0.01 || votingDifference < -0.01;

        let shouldShowInDropdown = false;
        let actionRequired = "";
        let suggestedChildren = [];
        let removalSuggestion = null;

        if (isIncrease) {
          // INCREASE: Unallocated ownership → Show in dropdown immediately
          shouldShowInDropdown = true;
          actionRequired = "allocate";

          suggestedChildren = calculateSuggestedRedistribution(
            parent,
            children,
            totalChildrenShares,
            parentTotalShares
          );
        } else if (isDecrease) {
          // DECREASE: Over-allocated → Do NOT show in dropdown initially
          shouldShowInDropdown = false;
          actionRequired = "reduce";

          suggestedChildren = calculateSuggestedRedistribution(
            parent,
            children,
            totalChildrenShares,
            parentTotalShares
          );

          // Also provide removal suggestions
          removalSuggestion = suggestShareholdersToRemove(
            children,
            parentTotalShares,
            totalChildrenShares
          );

          // Alternative: Find best removal combination
          if (!removalSuggestion?.exactMatch && children.length <= 10) {
            const bestCombo = findBestRemovalCombination(
              children,
              parentTotalShares
            );
            if (bestCombo?.exactMatch) {
              removalSuggestion = {
                ...removalSuggestion,
                bestCombination: bestCombo,
              };
            }
          }
        }

        // Update parent visibility based on logic
        if (shouldShowInDropdown) {
          if (
            !parent.showInBoDropdown &&
            !beneficialOwnersDropdown.some((b) => b._id === parent._id)
          ) {
            dispatch(
              updateDropdownEntryStatusAsync({
                beneficialOwnerId: parent._id,
                data: { showInBoDropdown: true },
              })
            );
          }
        } else {
          // For decrease, ensure parent is NOT in dropdown
          if (
            parent.showInBoDropdown ||
            beneficialOwnersDropdown.some((b) => b._id === parent._id)
          ) {
            dispatch(
              updateDropdownEntryStatusAsync({
                beneficialOwnerId: parent._id,
                data: { showInBoDropdown: false },
              })
            );
          }
        }

        // Store parent info for UI rendering
        parentsWithIssues.push({
          parent,
          children: suggestedChildren,
          sharesDifference,
          votingDifference,
          isIncrease,
          isDecrease,
          actionRequired,
          removalSuggestion,
          currentChildrenTotal: totalChildrenShares,
          targetTotal: parentTotalShares,
        });
      } else {
        // Conditions met - ensure parent is removed from dropdown
        if (
          parent.showInBoDropdown ||
          beneficialOwnersDropdown.some((b) => b._id === parent._id)
        ) {
          dispatch(
            updateDropdownEntryStatusAsync({
              beneficialOwnerId: parent._id,
              data: { showInBoDropdown: false },
            })
          );
        }
      }
    });

    setHasUnmetConditions(!conditionsMet);
    setParentsNeedingAttention(parentsWithIssues);

    const reportableBOAfterCumulative = filteredTempNonReportableBOs
      .filter((bo) => INDIVIDUALS.includes(bo.idType))
      .filter((bo) => {
        const { cumulativeShareholdings, cumulativeVotings } =
          calculateCumulativeOwnership(bo, processedBOs);
        if (cumulativeShareholdings >= 10 || cumulativeVotings >= 10) {
          const indexOfBO = filteredTempNonReportableBOs.findIndex(
            (beneficialOwner) => beneficialOwner._id === bo._id
          );
          if (indexOfBO !== -1) {
            filteredTempNonReportableBOs.splice(indexOfBO, 1);
          }
          return true;
        }
        return false;
      });

    setReportableBOWithChanges([
      ...filteredTempReportableBOs,
      ...reportableBOAfterCumulative,
    ]);
    setNonReportableBOWithChanges([
      ...filteredTempNonReportableBOs,
      ...jointShareholdersNonReportableBOs,
    ]);
    setDropdownBOWithChanges(
      processedBOs.filter((bo) => bo.showInBoDropdown && !bo.hidden)
    );
  }, [
    isInUpdateCompanyDetailsContext,
    transactions,
    beneficialOwners,
    reportableBO,
    nonReportableBO,
  ]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const isLinkedTo = (row) =>
    beneficialOwners.some(
      (bo) =>
        bo &&
        (bo.linkToCompany === row._id ||
          (bo.shareholderWithBO === row._id &&
            row.idType !== "Joint Shareholder"))
    );

  const handleEdit = async (row) => {
    if (
      isLinkedTo(row) &&
      !row?.linkToCompany &&
      !row?.shareholderWithBO &&
      row.mode === "editing"
    ) {
      alert("Please first remove linked beneficial owners");
      return;
    }
    if (row?.corporateIdType) setSelectedCorporateIDType(row.corporateIdType);
    else setSelectedCorporateIDType(null);
    if (!row.isViewOnly && !row?.isReadOnly) setIsViewOnly(false);
    else setIsViewOnly(true);
    if (row.shareholderWithBO && !row.JSGroupID) {
      const selectedShareholderWithBO = beneficialOwners.find(
        (bo) => bo._id === row.shareholderWithBO
      );
      const corporateShareholders = allBOs.filter(
        (bo) => bo.shareholderWithBO === row.shareholderWithBO
      );
      dispatch(resetCorporateShareholderTable());
      dispatch(
        addMultipleCorporateShareholder(
          _uniqBy(corporateShareholders, (official) => {
            if (official?.JSGroupID) return official.JSGroupID;
            else return official._id;
          })
        )
      );
      setSelectedDropdownBO(selectedShareholderWithBO);
      setShowBeneficialOwnersBtn(false);
      return;
    }

    let linkedJSFromDropdown = {};
    if (row?.shareholderWithBO && row?.JSGroupID && !row.linkToCompany) {
      linkedJSFromDropdown = allBOs.find(
        (bo) =>
          bo.JSGroupID &&
          bo.shareholderWithBO &&
          bo.JSGroupID === row.JSGroupID &&
          bo.idType === "Joint Shareholder"
      );
    }

    if (linkedJSFromDropdown?.shareholderWithBO) {
      if (isLinkedTo(linkedJSFromDropdown)) {
        alert("Please first remove linked beneficial owners");
        return;
      }
      const selectedShareholderWithBO = beneficialOwners.find(
        (bo) => bo._id === linkedJSFromDropdown.shareholderWithBO
      );
      const corporateShareholders = allBOs.filter(
        (bo) => bo.shareholderWithBO === linkedJSFromDropdown.shareholderWithBO
      );
      dispatch(resetCorporateShareholderTable());
      dispatch(
        addMultipleCorporateShareholder(
          _uniqBy(corporateShareholders, (official) => {
            if (official?.JSGroupID) return official.JSGroupID;
            else return official._id;
          })
        )
      );
      setSelectedDropdownBO(selectedShareholderWithBO);
      setShowBeneficialOwnersBtn(false);
      return;
    }

    if (!row.linkToCompany) {
      handleBackBtn();
      await delay();
      let companyShareholderEditBtn = null;
      let companyShareholderViewBtn = null;
      if (row?.JSGroupID) {
        companyShareholderEditBtn = document.querySelector(
          `[id="${row?.JSGroupID}"]`
        );
        companyShareholderViewBtn = document.querySelector(
          `[id="${row?.JSGroupID}_view"]`
        );
      } else if (row?._id && !row.JSGroupID) {
        companyShareholderEditBtn = document.querySelector(
          `[id="${row?._id}"]`
        );
        companyShareholderViewBtn = document.querySelector(
          `[id="${row?._id}_view"]`
        );
      }
      if (
        (row.isViewOnly && companyShareholderViewBtn) ||
        row.mode !== "editing"
      ) {
        companyShareholderViewBtn.click();
      } else if (companyShareholderEditBtn) companyShareholderEditBtn.click();
      return;
    }
    dispatch(openModal({ modalName: "beneficialOwner_edit_modal" }));
    setMode({ editing: true, id: row._id });
    setCurrentOfficial(row);
  };

  const handleDelete = async (row) => {
    if (isLinkedTo(row)) {
      alert("Please first remove linked beneficial owners");
      return;
    }
    if (row?.corporateIdType) setSelectedCorporateIDType(row.corporateIdType);
    else setSelectedCorporateIDType(null);
    if (row.shareholderWithBO && !row.JSGroupID) {
      const selectedShareholderWithBO = beneficialOwners.find(
        (bo) => bo._id === row.shareholderWithBO
      );
      const corporateShareholders = allBOs.filter(
        (bo) => bo.shareholderWithBO === row.shareholderWithBO
      );
      dispatch(resetCorporateShareholderTable());
      dispatch(
        addMultipleCorporateShareholder(
          _uniqBy(corporateShareholders, (official) => {
            if (official?.JSGroupID) return official.JSGroupID;
            else return official._id;
          })
        )
      );
      setSelectedDropdownBO(selectedShareholderWithBO);
      setShowBeneficialOwnersBtn(false);
      return;
    }

    let linkedJSFromDropdown = {};
    if (row?.shareholderWithBO && row?.JSGroupID && !row.linkToCompany) {
      linkedJSFromDropdown = allBOs.find(
        (bo) =>
          bo.JSGroupID &&
          bo.shareholderWithBO &&
          bo.JSGroupID === row.JSGroupID &&
          bo.idType === "Joint Shareholder"
      );
    }

    if (linkedJSFromDropdown?.shareholderWithBO) {
      if (isLinkedTo(linkedJSFromDropdown)) {
        alert("Please first remove linked beneficial owners");
        return;
      }
      const selectedShareholderWithBO = beneficialOwners.find(
        (bo) => bo._id === linkedJSFromDropdown.shareholderWithBO
      );
      const corporateShareholders = allBOs.filter(
        (bo) => bo.shareholderWithBO === linkedJSFromDropdown.shareholderWithBO
      );
      dispatch(resetCorporateShareholderTable());
      dispatch(
        addMultipleCorporateShareholder(
          _uniqBy(corporateShareholders, (official) => {
            if (official?.JSGroupID) return official.JSGroupID;
            else return official._id;
          })
        )
      );
      setSelectedDropdownBO(selectedShareholderWithBO);
      setShowBeneficialOwnersBtn(false);
      return;
    }

    if (!row.linkToCompany) {
      alert("Please edit inside the Company Officials tab");
      return;
    }

    await dispatch(
      deleteBeneficialOwnerAsync({
        beneficialOwnerId: row._id,
        companyId,
      })
    );
    const isInDropdown = beneficialOwnersDropdown.some(
      (bo) => bo._id === row.linkToCompany || bo._id === row?.linkToCompany?._id
    );
    if (!isInDropdown && row.linkToCompany) {
      const linkedCompany = beneficialOwners.find(
        (bo) =>
          bo._id === row?.linkToCompany || bo._id === row?.linkToCompany?._id
      );
      dispatch(
        updateDropdownEntryStatusAsync({
          beneficialOwnerId: linkedCompany._id,
          data: { showInBoDropdown: true },
        })
      );
    }
    setShowBeneficialOwnersBtn(true);
  };

  const selectedTab = tabs[selectedTabIndex];
  const tableData = isInUpdateCompanyDetailsContext
    ? selectedTab.id === "reportableBO"
      ? reportableBOWithChanges
      : nonReportableBOWithChanges
    : selectedTab.id === "reportableBO"
    ? reportableBO
    : nonReportableBO;

  useEffect(() => {
    if (
      !modals.includes("beneficialOwner_edit_modal") &&
      currentOfficial?._id
    ) {
      setMode({ id: "", editing: false });
      setCurrentOfficial({});
      setShowBeneficialOwnersBtn(true);
    }
  }, [modals, currentOfficial]);

  const handleSubmit = () => {
    if (beneficialOwnersDropdown.length === 0 && !hasUnmetConditions) {
      setBoTabValidationError("");
      handleNextBtn();
    } else {
      if (hasUnmetConditions) {
        setBoTabValidationError(
          "Please resolve all beneficial ownership distribution issues before proceeding."
        );
      } else {
        setBoTabValidationError(
          "Allocate indirect beneficial ownership interest."
        );
      }
    }
  };

  const handleGoBack = () => {
    if (isFirstStep) {
      handleBackBtn();
    } else {
      setSelectedTabIndex((prev) => prev - 1);
    }
  };

  const handleNextStep = () => {
    if (isLastStep) {
      handleSubmit();
    } else {
      setSelectedTabIndex((prev) => prev + 1);
    }
  };

  const checkForMissingBOsInTransactions = () => {
    if (
      !isInUpdateCompanyDetailsContext ||
      !transactions ||
      transactions.length === 0
    ) {
      return false;
    }

    const officialsNeedingBOEntry = [];

    transactions.forEach((transaction) => {
      if (
        transaction.shareCapitalChanges &&
        Array.isArray(transaction.shareCapitalChanges)
      ) {
        transaction.shareCapitalChanges.forEach((shareChange) => {
          if (
            shareChange.allocations &&
            Array.isArray(shareChange.allocations)
          ) {
            shareChange.allocations.forEach((allocation) => {
              if (
                allocation.officialId &&
                allocation.beneficialOwnershipDetails
              ) {
                const { updatedDetails } =
                  allocation.beneficialOwnershipDetails;

                const hasIndirectControl =
                  (updatedDetails?.indirectCompanyControlRight &&
                    updatedDetails.indirectCompanyControlRight !== "no") ||
                  (updatedDetails?.indirectPercentVotingRights &&
                    updatedDetails.indirectPercentVotingRights > 0) ||
                  (updatedDetails?.indirectPercentShareholding &&
                    updatedDetails.indirectPercentShareholding > 0) ||
                  (updatedDetails?.indirectRightRemoveDirector &&
                    updatedDetails.indirectRightRemoveDirector !== "no");

                if (hasIndirectControl) {
                  officialsNeedingBOEntry.push(allocation.officialId);
                }
              }
            });
          }
        });
      }
    });

    for (const officialId of officialsNeedingBOEntry) {
      const existsInBOs = allBOs.some((bo) => bo._id === officialId);
      if (!existsInBOs) {
        return true;
      }
    }

    return false;
  };

  const hasMissingBOs = checkForMissingBOsInTransactions();

  // ============================================================================
  // RENDER FUNCTIONS
  // ============================================================================

  const renderBOInformation = (showAddButton = false) => (
    <>
      {/* Capital Change Warnings */}
      {isInUpdateCompanyDetailsContext &&
        parentsNeedingAttention.length > 0 &&
        (() => {
          const visibleWarnings = parentsNeedingAttention.filter(
            (p) => !dismissedWarningIds.includes(p.parent._id)
          );

          if (visibleWarnings.length === 0) return null;

          const displayedWarnings = showAllWarnings
            ? visibleWarnings
            : visibleWarnings.slice(0, 2);

          const remainingCount =
            visibleWarnings.length - displayedWarnings.length;

          return (
            <div className="mb-4 space-y-3">
              {displayedWarnings.map((parentInfo, idx) => (
                <CapitalChangeWarning
                  key={parentInfo.parent._id || idx}
                  {...parentInfo}
                  onClose={() => {
                    setDismissedWarningIds((prev) => [
                      ...prev,
                      parentInfo.parent._id,
                    ]);
                  }}
                />
              ))}

              {remainingCount > 0 && (
                <div
                  className="bg-gray-50 border border-gray-200 p-3 rounded-md flex justify-between items-center cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => setShowAllWarnings(true)}
                >
                  <div className="flex items-center space-x-2">
                    <div className="bg-blue-100 p-1 rounded-full">
                      <ExclamationIcon className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-sm text-gray-700 font-medium">
                      +{remainingCount} more capital change alert
                      {remainingCount !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <span className="text-blue-600 text-sm font-semibold hover:underline">
                    View All
                  </span>
                </div>
              )}

              {showAllWarnings && visibleWarnings.length > 1 && (
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowAllWarnings(false)}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Show less
                  </button>
                </div>
              )}
            </div>
          );
        })()}

      {showBeneficialOwnersBtn && (
        <>
          <div className="mb-2 sm:-mt-4">
            <Tabs
              tabs={tabs}
              selectedTabIndex={selectedTabIndex}
              setSelectedTabIndex={setSelectedTabIndex}
            />
          </div>

          <BeneficialOwnersTable
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            data={tableData}
            isInUpdateCompanyDetailsContext={isInUpdateCompanyDetailsContext}
            showActionColumn
            handleBackBtn={handleBackBtn}
            selectedTableType={selectedTab.id}
            parentsNeedingAttention={parentsNeedingAttention}
          />
        </>
      )}

      {mode.editing && (
        <ModalWrapper
          name="beneficialOwner_edit_modal"
          title={isViewOnly ? "View Beneficial Owner" : "Edit Beneficial Owner"}
          maxWidth="sm:max-w-fit"
        >
          <BeneficialOwnerSelection
            selectedDropdownBO={selectedDropdownBO}
            setSelectedDropdownBO={setSelectedDropdownBO}
            setShowBeneficialOwnersBtn={setShowBeneficialOwnersBtn}
            mode={mode}
            currentOfficial={currentOfficial}
            selectedCorporateIDType={selectedCorporateIDType}
            isViewOnly={isViewOnly}
            isInUpdateCompanyDetailsContext={isInUpdateCompanyDetailsContext}
            dropdownData={
              isInUpdateCompanyDetailsContext ? dropdownBOWithChanges : null
            }
          />
        </ModalWrapper>
      )}

      {!showBeneficialOwnersBtn && !mode.editing ? (
        <BeneficialOwnerSelection
          selectedDropdownBO={selectedDropdownBO}
          setSelectedDropdownBO={setSelectedDropdownBO}
          setShowBeneficialOwnersBtn={setShowBeneficialOwnersBtn}
          mode={mode}
          currentOfficial={currentOfficial}
          selectedCorporateIDType={selectedCorporateIDType}
          isInUpdateCompanyDetailsContext={isInUpdateCompanyDetailsContext}
          dropdownData={
            isInUpdateCompanyDetailsContext ? dropdownBOWithChanges : null
          }
        />
      ) : (
        <div className="flex flex-col items-start mt-4">
          {beneficialOwnersDropdown.length > 0 &&
            !isInUpdateCompanyDetailsContext && (
              <button
                onClick={() => {
                  setShowBeneficialOwnersBtn(false);
                }}
                className="inline-flex items-center justify-center space-x-2 text-sm font-medium rounded-md text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed"
              >
                <PlusIcon className="w-6 h-6" aria-hidden="true" />
                <span>Add A Beneficial Owner</span>
              </button>
            )}
          {boTabValidationError &&
            (beneficialOwnersDropdown.length !== 0 || hasUnmetConditions) && (
              <p className="text-red-500 mt-2">{boTabValidationError}</p>
            )}
          {beneficialOwnersDropdown.length === 0 &&
            !selectedDropdownBO &&
            !showAddButton &&
            !hasUnmetConditions && (
              <p className="text-sm">There is no BO to be allocated</p>
            )}
        </div>
      )}
    </>
  );

  const renderDefaultView = () => (
    <div className="relative">
      {(deleteBeneficialOwnerStatus.inProgressIds.length > 0 ||
        addBeneficialOwnerStatus.inProgressIds.length > 0 ||
        getAllBeneficialOwnersStatus.status === "loading" ||
        updateDropdownEntryStatusStatus.status === "loading") && <Overlay />}

      {renderBOInformation()}

      {showBeneficialOwnersBtn && (
        <div className="flex justify-between mt-4">
          <button
            type="button"
            onClick={handleGoBack}
            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gray-600 border border-transparent rounded-md shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Go back
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            onClick={handleNextStep}
            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <SpinnerIcon />
                Submitting...
              </>
            ) : (
              submitBtnLabel
            )}
          </button>
        </div>
      )}
    </div>
  );

  const renderUpdateCompanyDetailsView = () => (
    <div className="relative">
      {(deleteBeneficialOwnerStatus.inProgressIds.length > 0 ||
        addBeneficialOwnerStatus.inProgressIds.length > 0 ||
        getAllBeneficialOwnersStatus.status === "loading" ||
        updateDropdownEntryStatusStatus.status === "loading") && <Overlay />}

      {renderBOInformation(hasMissingBOs || hasUnmetConditions)}

      {(beneficialOwnersDropdown.length > 0 ||
        hasMissingBOs ||
        hasUnmetConditions) &&
        showBeneficialOwnersBtn && (
          <div className="flex flex-col items-start mt-4">
            <button
              onClick={() => {
                setShowBeneficialOwnersBtn(false);
              }}
              className="inline-flex items-center justify-center space-x-2 text-sm font-medium rounded-md text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed"
            >
              <PlusIcon className="w-6 h-6" aria-hidden="true" />
              <span>Add A Beneficial Owner</span>
            </button>
            {hasMissingBOs && (
              <p className="text-sm text-gray-600 mt-2">
                Some officials from transactions are missing beneficial
                ownership information.
              </p>
            )}
          </div>
        )}

      {showBeneficialOwnersBtn && (
        <div className="flex justify-between mt-4">
          <button
            type="button"
            onClick={handleGoBack}
            className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
          >
            <span className="flex items-center mr-4 h-7">
              <ChevronLeftIcon
                className="flex-shrink-0 w-5 h-5"
                aria-hidden="true"
              />
            </span>
            <p className="font-semibold">Go back</p>
          </button>
          <button
            type="submit"
            disabled={isSubmitting || hasUnmetConditions}
            onClick={handleNextStep}
            className="flex items-center px-4 py-2 mb-2 font-semibold text-white rounded bg-sflPrimary mt-7 disabled:cursor-not-allowed disabled:opacity-50"
            title={
              hasUnmetConditions
                ? "Resolve all ownership issues to proceed"
                : ""
            }
          >
            {isSubmitting ? (
              <>
                <SpinnerIcon />
                Submitting...
              </>
            ) : (
              submitBtnLabel
            )}
          </button>
        </div>
      )}
    </div>
  );

  switch (isInUpdateCompanyDetailsContext) {
    case true:
      return renderUpdateCompanyDetailsView();
    case false:
    default:
      return renderDefaultView();
  }
};

export default BeneficialOwnershipTab;
